/**
 * EMIS/LESMIS Export Utility
 * ສ້າງໄຟລ໌ Excel ທີ່ສອດຄ່ອງກັບຮູບແບບ EMIS StudentProfile
 */

import * as XLSX from 'xlsx';
import {
  EMIS_ENROLLMENT_STATUS,
  EMIS_DISABILITY,
  EMIS_DISABILITY_TYPE,
  EMIS_ETHNICITY,
  EMIS_GRADE_CODE,
} from './emisValidator';

// Map ຄ່າ gender ຈາກ DB ໄປ EMIS
function mapGender(g) {
  if (g === 'ຊ' || g === 1) return 1;
  if (g === 'ຍ' || g === 2) return 2;
  return '';
}

/**
 * ແປງ student object ຈາກ DB ໄປ row ຕາມ EMIS StudentProfile
 */
function toEmisRow(s, index) {
  const gradeCode = Number(s.Grade_Code);
  const enStatus = Number(s.enrollment_status);
  const ethCode = Number(s.ethnicity_code);
  const disCode = Number(s.disability);
  const disType = Number(s.disability_type);

  return {
    'ລຳດັບ': index + 1,
    // Common fields
    'ສົກຮຽນ (Q.1)': s.accd || s.accd_current || '',
    'ລະຫັດໂຮງຮຽນ (Q.2)': s.school_id || '',
    // StudentProfile fields
    'ລະຫັດນັກຮຽນ': s.student_id || '',
    'ຊື່ (ລາວ) (Q.8)': s.firstNameLao || '',
    'ນາມສະກຸນ (ລາວ) (Q.9)': s.familyNameLao || '',
    'ຊື່ (ອັງກິດ)': s.nameEnglish || '',
    'ນາມສະກຸນ (ອັງກິດ)': s.familyNameEnglish || '',
    'ວ/ດ/ປ ເກີດ (Q.11)': s.dob || '',
    'ເພດ (Q.12) 1=ຊ 2=ຍ': mapGender(s.gender),
    'ຊັ້ນຮຽນ Code (Q.14)': gradeCode || '',
    'ຊັ້ນຮຽນ ຊື່': EMIS_GRADE_CODE[gradeCode] || '',
    'ຫ້ອງ': s.room_no || '',
    // ທີ່ຢູ່ປັດຈຸບັນ
    'ລະຫັດແຂວງປັດຈຸບັນ (Q.17)': s.cur_prov_id || '',
    'ລະຫັດເມືອງປັດຈຸບັນ (Q.18)': s.cur_district_id || '',
    'ຕາແສງປັດຈຸບັນ (Q.19)': s.cur_commune || '',
    'ບ້ານປັດຈຸບັນ (Q.20)': s.cur_village || '',
    // ທີ່ຢູ່ເກີດ
    'ລະຫັດແຂວງເກີດ (Q.22)': s.b_prov_id || '',
    'ລະຫັດເມືອງເກີດ (Q.23)': s.b_district_id || '',
    'ຕາແສງເກີດ (Q.24)': s.b_commune || '',
    'ບ້ານເກີດ (Q.25)': s.b_village || '',
    // EMIS-specific
    'ສະຖານະລົງທະບຽນ Code (Q.32)': enStatus || '',
    'ສະຖານະລົງທະບຽນ ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[enStatus] || '',
    'ຊົນເຜົ່າ Code (Q.34)': ethCode || '',
    'ຊົນເຜົ່າ ຊື່': EMIS_ETHNICITY[ethCode] || '',
    'ຄວາມພິການ Code (Q.35)': disCode || '',
    'ຄວາມພິການ ຄຳອະທິບາຍ': EMIS_DISABILITY[disCode] || '',
    'ປະເພດຄວາມພິການ Code (Q.36)': disCode === 1 ? (disType || '') : '',
    'ປະເພດຄວາມພິການ ຄຳອະທິບາຍ': disCode === 1 ? (EMIS_DISABILITY_TYPE[disType] || '') : 'N/A',
  };
}

/**
 * Export ລາຍຊື່ນັກຮຽນ ອອກເປັນ EMIS Excel
 * @param {Array} students - array ຂອງ student objects
 * @param {string} filename - ຊື່ໄຟລ໌ (ໂດຍບໍ່ມີ .xlsx)
 */
export function exportEmisStudents(students, filename = 'EMIS_StudentProfile') {
  if (!students || students.length === 0) {
    alert('ບໍ່ມີຂໍ້ມູນນັກຮຽນທີ່ຈະ Export');
    return;
  }

  const rows = students.map((s, i) => toEmisRow(s, i));

  const ws = XLSX.utils.json_to_sheet(rows);

  // ກຳນົດຄວາມກວ້າງ column
  const colWidths = Object.keys(rows[0]).map(key => ({
    wch: Math.max(key.length * 1.8, 16),
  }));
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'StudentProfile');

  // ເພີ່ມ sheet ສຳລັບ Reference codes
  const refData = [
    { 'Sheet': 'Reference', 'ໝວດ': 'ສະຖານະລົງທະບຽນ (Q.32)', 'ລະຫັດ': 1, 'ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[1] },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 2, 'ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[2] },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 3, 'ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[3] },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 4, 'ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[4] },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 5, 'ຄຳອະທິບາຍ': EMIS_ENROLLMENT_STATUS[5] },
    { 'Sheet': '', 'ໝວດ': 'ຄວາມພິການ (Q.35)', 'ລະຫັດ': 1, 'ຄຳອະທິບາຍ': 'ມີ' },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 2, 'ຄຳອະທິບາຍ': 'ບໍ່ມີ' },
    { 'Sheet': '', 'ໝວດ': '', 'ລະຫັດ': 3, 'ຄຳອະທິບາຍ': 'ບໍ່ແນ່ໃຈ' },
    ...Object.entries(EMIS_ETHNICITY).map(([k, v]) => ({
      'Sheet': '', 'ໝວດ': k === '1' ? 'ຊົນເຜົ່າ (Q.34)' : '', 'ລະຫັດ': Number(k), 'ຄຳອະທິບາຍ': v,
    })),
  ];
  const wsRef = XLSX.utils.json_to_sheet(refData);
  XLSX.utils.book_append_sheet(wb, wsRef, 'Reference Codes');

  const ts = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${filename}_${ts}.xlsx`);
}
